import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { dxDiagnosisResultApiClient } from '@src/api/tenant/dxDiagnosis/dxDiagnosisResultApiClient';
import { DxDiagnosisResult } from '@src/api/types/dxDiagnosisResult';
import { Pagination } from '@src/api/types/pagination';
// import SvgIconDownload from '@src/assets/images/common/icon/chat-action/SvgIconDownload';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { DxDiagnosisResultStatus } from '@src/enum/dxDiagnosisResultStatus';
import { useToast } from '@src/features/toast/context/toastContext';
// import Button from '@src/ui/elements/button/Button';
// import DropdownCheckbox from '@src/ui/elements/form/DropdownCheckbox';
import InputSearch from '@src/ui/elements/form/InputSearch';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title';
import logger from '@src/utils/logger';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DxDiagnosisListForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [diagnoses, setDiagnoses] = useState<DxDiagnosisResult[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false); // 初期値をfalseに設定
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [keyword, setKeyword] = useState<string>('');
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([1, 2, 3]); // Default: すべて選択
  const [isExporting, setIsExporting] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'すべて' },
    { value: '1', label: '回答中' },
    { value: '2', label: '回答済み' },
    { value: '3', label: '削除済み' },
  ];

  const handleSelectStatus = (selectedOptions: { value: string; label: React.ReactNode }[]) => {
    const selectedValues = selectedOptions
      .filter((option) => option.value !== 'all')
      .map((option) => parseInt(option.value));
    setSelectedStatuses(selectedValues);
  };

  const loadDiagnoses = async (
    page = 1,
    sortField: string | null = null,
    sortDirection: 'asc' | 'desc' = 'asc',
    keyword: string | null = null,
  ) => {
    const params: { page: number; sort?: string; direction?: 'asc' | 'desc'; keyword?: string } = {
      page,
    };
    if (sortField) {
      params.sort = sortField;
    }
    if (sortDirection) {
      params.direction = sortDirection;
    }
    if (keyword) {
      params.keyword = keyword;
    }

    setLoading(true);

    const res = await dxDiagnosisResultApiClient.index(params);
    handleApiResponse(res, (data) => {
      setDiagnoses(data.data);
      setPagination(data.pagination);
      setFilteredCount(data.pagination?.total || 0);
      setSearchedKeyword(keyword || '');
    });

    setLoading(false);
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const page = parseInt(query.get('page') || '1', 10);
    const sort = query.get('sort') || 'id';
    const direction = (query.get('direction') as 'asc' | 'desc') || 'desc';
    const keyword = query.get('keyword') || '';

    setSortField(sort);
    setSortDirection(direction || 'asc');
    setKeyword(keyword);
    setSearchedKeyword(keyword);
    loadDiagnoses(page, sort, direction || 'asc', keyword);
  }, [location.search]);

  const handlePageChange = (page: number) => {
    const currentPage = new URLSearchParams(location.search).get('page');
    if (currentPage === page.toString()) {
      return;
    }
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  const handleSortChange = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    const query = new URLSearchParams(location.search);
    query.set('sort', field);
    query.set('direction', newDirection);
    query.set('page', '1'); // ソート変更時はページを1にリセット
    navigate({ search: query.toString() });
  };

  const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const handleKeywordSearch = () => {
    const query = new URLSearchParams(location.search);
    const currentKeyword = query.get('keyword');
    if (currentKeyword === keyword.trim()) {
      return;
    }

    setSearchedKeyword(keyword);
    setFilteredCount(null);
    query.set('keyword', keyword);
    query.set('page', '1');
    navigate({ search: query.toString() });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleKeywordSearch();
    }
  };

  const handleCsvExport = async () => {
    setIsExporting(true);

    try {
      const params: {
        status?: number[];
        keyword?: string;
      } = {};

      const hasAllStatuses = selectedStatuses.length === 0 || selectedStatuses.length === 3;
      if (!hasAllStatuses) {
        params.status = selectedStatuses;
      }

      if (searchedKeyword) {
        params.keyword = searchedKeyword;
      }

      const response = await dxDiagnosisResultApiClient.exportCsv(params);

      handleApiResponse<Blob, never>(
        response,
        (csvBlob) => {
          const bom = '\uFEFF';
          const blobWithBom = new Blob([bom, csvBlob], { type: 'text/csv;charset=utf-8;' });

          const blobUrl = window.URL.createObjectURL(blobWithBom);
          const anchorElement = document.createElement('a');
          anchorElement.href = blobUrl;
          const fileNamePrefix = 'DX診断結果一覧';
          const currentData = dayjs().format('YYYYMMDD_HHmmss');
          anchorElement.download = `${fileNamePrefix}_${currentData}.csv`;
          document.body.appendChild(anchorElement);
          anchorElement.click();
          document.body.removeChild(anchorElement);
          window.URL.revokeObjectURL(blobUrl);
        },
        (error) => {
          logger.error('CSVのエクスポートに失敗しました。:', error);
          toast('CSVのエクスポートに失敗しました。');
        },
      );
    } catch (error) {
      logger.error('CSVのエクスポートに失敗しました。:', error);
      toast('CSVのエクスポートに失敗しました。');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main>
      <SpinnerOverlay loading={loading} />
      <div>
        <div className='flex items-center justify-between'>
          <Title>DX診断結果一覧</Title>
          <div className='flex items-center gap-4'>
            <div className='w-[250px]'>
              <InputSearch
                key={searchedKeyword}
                value={keyword}
                defaultValue={searchedKeyword}
                onChange={handleKeywordChange}
                onKeyDown={handleKeyDown}
                onSubmit={handleKeywordSearch}
                onClear={() => {
                  setKeyword('');
                  navigate({ search: '' });
                }}
              />
            </div>
          </div>
        </div>

        {/* <div className='mt-[10px] flex items-center justify-end'>
          <div className='flex items-center gap-3'>
            <DropdownCheckbox
              className='!w-[160px] !py-3'
              options={statusOptions}
              onSelect={handleSelectStatus}
              allSelectedText='すべて'
              selectedCountText={(count) => `${count}件のステータス`}
              placeholder='ステータス'
              initialSelectedOptions={statusOptions}
              autoSelectAll={false}
            />
            <Button
              size={isExporting ? 'l' : 's'}
              className='!px-3 !py-2'
              onClick={handleCsvExport}
              loading={isExporting}
            >
              <SvgIconDownload />
            </Button>
          </div>
        </div> */}

        <div className='mt-10'></div>
        {searchedKeyword && (
          <p className='overflow-wrap-anywhere mb-4'>
            「{searchedKeyword}」で絞り込んだ結果：{filteredCount}件
          </p>
        )}
        <div className='overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[100px]' />
              <col className='w-[120px]' />
              <col className='w-[120px]' />
              <col className='min-w-[125px]' />
              <col className='w-[120px]' />
              <col className='w-[108px]' />
              <col className='w-[124px]' />
              <col className='w-[180px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sortField === 'id' ? sortDirection : null}
                  onClick={() => handleSortChange('id')}
                >
                  診断ID
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'created_at' ? sortDirection : null}
                  onClick={() => handleSortChange('created_at')}
                >
                  開始日
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'diagnosis_at' ? sortDirection : null}
                  onClick={() => handleSortChange('diagnosis_at')}
                >
                  完了日
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'company_name_kana' ? sortDirection : null}
                  onClick={() => handleSortChange('company_name_kana')}
                >
                  企業名
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'companyUser.fullname_kana' ? sortDirection : null}
                  onClick={() => handleSortChange('companyUser.fullname_kana')}
                >
                  回答者
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'version' ? sortDirection : null}
                  onClick={() => handleSortChange('version')}
                >
                  診断ver
                </Th>
                <Th
                  sortable
                  sortOrder={sortField === 'status' ? sortDirection : null}
                  onClick={() => handleSortChange('status')}
                >
                  ステータス
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {diagnoses.length > 0
                ? diagnoses.map((diagnosis) => (
                    <tr
                      key={diagnosis.id}
                      className='[&:nth-child(2n)]:bg-gray-10'
                    >
                      <td>
                        <div className='px-4 py-6'>{diagnosis.id}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{diagnosis.created_at}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{diagnosis.diagnosis_on}</div>
                      </td>
                      <td>
                        <div className='truncate px-4 py-6'>{diagnosis.company.name}</div>
                      </td>
                      <td>
                        <div className='truncate px-4 py-6'>
                          {diagnosis.company_user.family_name} {diagnosis.company_user.first_name}
                        </div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>{diagnosis.version}</div>
                      </td>
                      <td>
                        <div className='px-4 py-6'>
                          {diagnosis.status_label ||
                            (diagnosis.status === DxDiagnosisResultStatus.IN_PROGRESS
                              ? '回答中'
                              : '回答済み')}
                        </div>
                      </td>
                      <td>
                        {diagnosis.status === DxDiagnosisResultStatus.COMPLETED &&
                          diagnosis.status_label !== '削除済み' && (
                            <div className='grid h-full place-items-center'>
                              <button
                                className='flex rounded border border-solid border-gray-200 bg-white px-6 py-[10px] text-sm'
                                onClick={() =>
                                  navigate(`/tenant/dx-diagnosis/${diagnosis.id}/results`)
                                }
                              >
                                診断結果を見る
                              </button>
                            </div>
                          )}
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {!searchedKeyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
        </div>
        {/* ページング */}
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
};
export default DxDiagnosisListForm;
